import { useState, useEffect, useMemo, useCallback } from 'react';
import { Property, AppUser, UserRole } from '../../../types';
import { parseScheduleString } from '../../../utils/scheduleParser';
import { cleanPhoneNumber } from '../../../utils/formatters';
import { submitVisitRequest, createZohoVisitTask } from '../../../services/visitService';
import { fetchInmobiliariasConfigApi } from '../../../services/configApiClient';
import { useAuth } from '../../../hooks/useAuth';
import { isNegociacionEspecial } from './visitNegotiationUtils';
import {
  GOOGLE_SCRIPT_URL,
  DEFAULT_GLOBAL_SCHEDULE,
  buildKeyOptionDescription,
  buildBrokerZohoPayload,
  buildWorkflowPayload,
} from './visitPayloadBuilder';

export const useScheduleVisitLogic = (isOpen: boolean, property: Property, initialAppUser: AppUser | null) => {
  const { appUser: currentAppUser } = useAuth();
  const [status, setStatus] = useState<'form' | 'confirmation' | 'loading' | 'success' | 'error' | 'fetching_config'>('form');
  const [visitType, setVisitType] = useState<'reconocimiento' | 'cliente' | null>(null);
  const [visitConfig, setVisitConfig] = useState<any>(null);
  const [errorData, setErrorData] = useState<{ message: string; requestId: string } | null>(null);

  const activeAppUser = currentAppUser || initialAppUser;
  const isSpecialNegotiation = useMemo(() => isNegociacionEspecial(property), [property]);

  const isBrokerProfile = useMemo(
    () =>
      activeAppUser?.role === UserRole.BROKER ||
      activeAppUser?.role === UserRole.BROKER_INTERNO ||
      activeAppUser?.role === UserRole.BROKER_PREMIUM,
    [activeAppUser]
  );
  const isClientView = useMemo(() => activeAppUser?.role === UserRole.CLIENTE, [activeAppUser]);

  const initialFormState = useMemo(
    () => ({
      nombre_broker: isClientView ? '' : activeAppUser?.nombreCompleto || '',
      cc_broker: activeAppUser?.numeroIdentificacion || '',
      countryCode_broker: activeAppUser?.complementoCelular || '+57',
      celular_broker: isClientView ? '' : cleanPhoneNumber(activeAppUser?.celular || ''),
      email_broker: isClientView ? '' : activeAppUser?.email || '',
      nombre_cliente: '', last_name: '', email_cliente: '', document_cliente: '',
      countryCode_cliente: '+57', celular_cliente: '', fecha1: '', hora1: '', fecha2: '', hora2: '',
      ciudad: property.ciudad || '', acompaniantes: '', descripcion: '',
      llaves_quien: 'broker', llaves_nombre: '', llaves_telefono: '', llaves_documento: '',
    }),
    [isClientView, activeAppUser, property.ciudad]
  );

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormState);
      setStatus('form');
      setVisitType(null);
    }
  }, [isOpen, initialFormState]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!isOpen || !property.codigo) return;
      setStatus('fetching_config');
      try {
        const inmoConfig = await fetchInmobiliariasConfigApi();
        const inmoData = inmoConfig[property.owner] || {};
        const scheduleStr = inmoData.horario || DEFAULT_GLOBAL_SCHEDULE;
        let scheduleData = parseScheduleString(scheduleStr);
        const customSchedule = parseScheduleString(property.disponibilidad_visitas);
        if (customSchedule) scheduleData = customSchedule;
        setVisitConfig({ HORARIO: scheduleData });
      } catch (err) {
        setVisitConfig({ HORARIO: parseScheduleString(DEFAULT_GLOBAL_SCHEDULE) });
      }
      setStatus('form');
    };
    fetchConfig();
  }, [isOpen, property.codigo, property.owner, property.disponibilidad_visitas]);

  const handleSelectVisitType = useCallback(
    (type: 'reconocimiento' | 'cliente') => {
      if (type === 'reconocimiento' && isSpecialNegotiation) return;
      setVisitType(type);
    },
    [isSpecialNegotiation]
  );

  const handleSubmit = async () => {
    setStatus('loading');
    setErrorData(null);
    try {
      if (visitType === 'reconocimiento' && isSpecialNegotiation) {
        throw new Error('Las visitas de reconocimiento no están disponibles para inmuebles en negociación especial.');
      }
      const finalDescription = buildKeyOptionDescription(formData);
      if (isBrokerProfile) {
        const payload = buildBrokerZohoPayload(property, formData, activeAppUser, visitType, finalDescription);
        await createZohoVisitTask(payload);
      } else {
        const payload = buildWorkflowPayload(property, formData, isClientView, finalDescription);
        await submitVisitRequest(GOOGLE_SCRIPT_URL, payload);
      }
      setStatus('success');
    } catch (err: any) {
      setErrorData({ message: err?.message || 'Error desconocido', requestId: err?.requestId || 'N/A' });
      setStatus('error');
    }
  };

  const minDate = useMemo(() => new Date(new Date().setDate(new Date().getDate() + 2)).toISOString().split('T')[0], []);

  return {
    status, setStatus, visitType, setVisitType, handleSelectVisitType,
    formData, setFormData, visitConfig, errorData, minDate, handleSubmit,
    isBrokerProfile, isClientView, isSpecialNegotiation,
  };
};
